import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { sendMessage } from '../chat/chatSlice';
import type { RootState } from '@/app/store';

export interface OrderItem {
  id: string; // Corresponds to uploadedFileId
  orderId?: string; // Reference to the order it belongs to
  uploadedFileId?: string; // Reference to the uploaded file
  paperTypeId?: string; // ID of the paper type
  printerId?: string; // ID of the printer
  width?: number; // Width in mm or units
  height?: number; // Height in mm or units
  colorMode?: 'COLOR' | 'BLACK_WHITE'; // Match enum
  status?: 'configuring' | 'configured'; // Status in frontend context
  originalWidthPx?: number;
  originalHeightPx?: number;
  createdAt?: Date; // Timestamp when created
  updatedAt?: Date; // Timestamp when last updated
}

interface OrderState {
  id?: string; // Unique identifier from backend
  status?: string; // e.g., 'NEW', 'CONFIGURING' to match OrderStatus enum
  serviceId?: string; // ID of the associated service
  deliveryAddress?: string; // e.g., '123 Main St, City, Country'
  paymentInformation?: {
    method?: string; // e.g., 'credit_card', 'paypal'
    status?: string; // e.g., 'pending', 'completed'
  };
  items: OrderItem[];
}

const initialState: OrderState = {
  items: [],
};

export const orderSlice = createSlice({
  name: 'order',
  initialState,
  reducers: {
    addOrderItem: (
      state,
      action: PayloadAction<{
        uploadedFileId: string;
        originalWidthPx?: number;
        originalHeightPx?: number;
      }>
    ) => {
      const existingItem = state.items.find(item => item.id === action.payload.uploadedFileId);
      if (!existingItem) {
        state.items.push({
          id: action.payload.uploadedFileId,
          status: 'configuring',
          originalWidthPx: action.payload.originalWidthPx,
          originalHeightPx: action.payload.originalHeightPx,
          // Default empty values
          paperTypeId: '',
          printerId: '',
          width: 0,
          height: 0,
          colorMode: 'COLOR',
        });
      }
    },
        updateOrderItem: (state, action: PayloadAction<{ uploadedFileId: string; data: Partial<OrderItem> }>) => {
      const itemIndex = state.items.findIndex(item => item.id === action.payload.uploadedFileId);
      if (itemIndex !== -1) {
        state.items[itemIndex] = {
          ...state.items[itemIndex],
          ...action.payload.data,
          status: 'configured',
        };
      }
    },
    clearOrder: (state) => {
      state.items = [];
    },
    setOrderState: (_, action: PayloadAction<OrderState>) => {
      // Directly replace the state with the payload from the backend
      return action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(sendMessage.fulfilled, (_state, action) => {
      if (action.payload.uiPayload?.updatedState) {
        // The backend is the source of truth. We replace our local state
        // with the state provided in the AI's response.
        return action.payload.uiPayload.updatedState;
      }
    });
  },
});

export const { addOrderItem, updateOrderItem, clearOrder, setOrderState } = orderSlice.actions;

export const selectOrderItems = (state: RootState) => state.order.items;
export const selectOrderState = (state: RootState) => state.order;

export default orderSlice.reducer;
